import { useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import Api from '../Network';
import { UseVerificationCodeReturn } from '../types/types';

const useVerificationCode = (): UseVerificationCodeReturn => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Verification code expiration time (5 minutes)
  const CODE_EXPIRATION_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Get stored verification code
  const getStoredCode = (): string | null => {
    return localStorage.getItem('verificationCode');
  };

  // Get stored phone number
  const getStoredPhone = (): string | null => {
    return localStorage.getItem('verificationPhone');
  };

  // Get stored timestamp
  const getStoredTimestamp = (): number | null => {
    const timestamp = localStorage.getItem('verificationTimestamp');
    return timestamp ? parseInt(timestamp, 10) : null;
  };

  // Check if code is expired
  const isCodeExpired = (): boolean => {
    const timestamp = getStoredTimestamp();
    if (!timestamp) return true;

    const currentTime = Date.now();
    const timeDifference = currentTime - timestamp;

    return timeDifference > CODE_EXPIRATION_TIME;
  };

  // Clear stored verification data
  const clearStoredCode = (): void => {
    localStorage.removeItem('verificationCode');
    localStorage.removeItem('verificationPhone');
    localStorage.removeItem('verificationTimestamp');
  };

  // Verify the input code
  const verifyCode = async (inputCode: string, phoneNumber: string): Promise<boolean> => {
    setLoading(true);

    try {
      // Call backend API to verify code
      const response: any = await Api.post('/api/password/otp/verify', {
        phone: phoneNumber,
        role: 'admin',
        otp: inputCode,
      });

      // Debug: log the response
      console.log('OTP Verify Response:', response);

      // Check for success - handle both 'success' and 'status' fields
      if (response && (response.success === true || response.status === true)) {
        message.success(response?.message || t('auth.verificationSuccess'));
        return true;
      } else {
        const errorMsg = response?.message || response?.msg || t('auth.verificationFailed');
        message.error(errorMsg);
        return false;
      }
    } catch (error: any) {
      // Log the full error for debugging
      console.error('OTP Verify Error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message ||
        t('auth.verificationFailed');
      message.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Auto-clear expired codes on component mount
  useEffect(() => {
    if (isCodeExpired()) {
      clearStoredCode();
    }
  }, []);

  return {
    verifyCode,
    clearStoredCode,
    getStoredCode,
    getStoredPhone,
    isCodeExpired,
    loading,
  };
};

export default useVerificationCode;
